import { useCallback, useMemo } from 'react';
import {
  Create,
  Edit,
  SaveButton,
  SelectInput,
  SelectArrayInput,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useRecordContext,
  regex,
  useGetIdentity,
} from 'react-admin';
import { useLogEffect } from '@/util/useLogEffect';
import { BSON } from 'realm-web';
import { useFormContext } from 'react-hook-form';
import { JsonInput } from '@/components/JsonInput';

export const entityTypes = {
  therapistSessionNotes: 'Therapist - Session Notes',
  therapistHomework: 'Therapist - Homework',
  journalEntry: 'User - Journal Entry',
};

export const entityTypesChoices = Object.entries(entityTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

const entityTypesByAuthorType = {
  therapist: ['therapistSessionNotes', 'therapistHomework'],
  user: ['journalEntry'],
} as const;

export const authorTypes = {
  therapist: 'Therapist',
  user: 'Journal User',
};

export const getAvailableEntityTypes = (
  authorType: keyof typeof entityTypesByAuthorType,
) => entityTypesByAuthorType[authorType];

export const authorTypesChoices = Object.entries(authorTypes).map(
  ([id, name]) => ({
    id,
    name,
  }),
);

export const AIPromptTagsEdit = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        updatedBy: userId,
        updatedAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Edit
      title={<EditTitle />}
      mutationMode="pessimistic"
      actions={false}
      transform={transform}
    >
      <AIPromptTagsEditForm />
    </Edit>
  );
};

const regexString = '^[a-zA-Z0-9$%._-]+$';

export const AIPromptTagsCreate = () => {
  const userId = useGetIdentity().data?.id?.toString();

  const transform = useCallback(
    (data: any) => {
      console.log('data', data);

      return {
        ...data,
        _id: new BSON.ObjectId(),
        // entityType: "therapistSessionNotes",
        createdBy: userId,
        createdAt: new Date(),
      };
    },
    [userId],
  );

  return (
    <Create
      redirect="list"
      transform={transform}
      title={<EditTitle />}
    >
      <AIPromptTagsEditForm />
    </Create>
  );
};

export const EditToolbar = () => (
  <Toolbar>
    <SaveButton label="Save" />
  </Toolbar>
);

export const EditTitle = () => {
  const record = useRecordContext();

  return <span>Prompt Tag: {record ? `${record.id}` : ''}</span>;
};

const AIPromptTagsEditForm = () => {
  const record = useRecordContext();

  useLogEffect('record', record);

  return (
    <SimpleForm record={record} toolbar={<EditToolbar />}>
      <AIPromptTagsFormFields />
    </SimpleForm>
  );
};

export const tagNameRegexExpr = `^\@(owner|asset)[.][a-zA-Z0-9._-]+$`;

// New component that can access form context
const AIPromptTagsFormFields = () => {
  const { watch, formState } = useFormContext();

  // Watch specific fields
  const authorType = watch('authorType');

  const tagName = watch('tagName');

  useLogEffect('formState errors', formState.errors);

  const availableEntityTypes = useMemo(() => {
    const types = getAvailableEntityTypes(
      authorType as keyof typeof entityTypesByAuthorType,
    );

    const result = (types || []).map(type => ({
      id: type,
      name: entityTypes[type],
    }));

    console.log('result', result);

    return result;
  }, [authorType]);

  const authorTypeRegexType = useMemo(
    () => new RegExp(tagNameRegexExpr, 'g'),
    [],
  );

  return (
    <>
      <SelectInput
        label="Author Type"
        source="authorType"
        choices={authorTypesChoices}
        validate={[required()]}
      />

      <SelectArrayInput
        label="Asset Type"
        source="assetType"
        choices={availableEntityTypes}
        validate={[required()]}
      />

      <TextInput
        label="Tag Name"
        source="tagName"
        sx={w300px}
        validate={[
          required(),
          regex(
            authorTypeRegexType,
            `Must conform to ${tagNameRegexExpr}`,
          ),
        ]}
      />

      <TextInput label="Tag Field" source="tagField" sx={w300px} />

      <JsonInput
        label="Tag Expression"
        source="tagExpression"
        // multiline={true}
        // rows={10}
        // sx={w300px}
        // format={(value) =>
        //   typeof value === "string" || !value ? value : JSON.stringify(value)
        // }
        // parse={(value) => {
        //   try {
        //     return JSON.parse(value);
        //   } catch (e) {
        //     try {
        //       const parsed = JSON.stringify(eval(value));

        //       return JSON.parse(parsed);
        //     } catch (e) {
        //       return value;
        //     }
        //   }
        // }}
      />
    </>
  );
};

const w300px = { minWidth: '300px' };

const frameworks = {
  all: 'General',
  soap: 'SOAP',
};

const outputTypes = [
  'grammar',
  'bullet',
  'singleSentence',
  'tags',
  'title',
] as const;

const outputTypesChoices = outputTypes.map(id => ({ id, name: id }));
